/**
 * ARKO-Chat „Analysieren“: Upload → Extract → kombinierter documentContext (n8n Goldener Pfad).
 */

import {
  PORTAL_DOCUMENT_UPLOAD_URL,
  PORTAL_ONLYOFFICE_EXTRACT_TEXT_URL,
  PORTAL_PLAINTEXT_FETCH_URL,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  portalAuthzSidecarExpected,
  resolveOnlyofficeIssueJwtUrl,
} from '../../constants';
import {
  fileExtensionFromName,
  isAudioFile,
  isPortalPlainTextExtension,
  parsePortalDocumentN8nResponse,
  type PortalDocumentOpenFields,
} from './portalDocuments';
import { prepareAudioAnalyzeDocumentContext, validatePortalAudioAnalyzeFile } from './portalAudioAnalyze';

/** Max. Anhänge pro Analyse-Nachricht. */
export const PORTAL_CHAT_ANALYZE_MAX_FILES = 5;

/** Pro Datei (Upload-Webhook / NC). */
export const PORTAL_CHAT_ANALYZE_MAX_FILE_BYTES = 25 * 1024 * 1024;

/** Gesamt-Budget für kombinierten documentContext (mehrere Dateien). */
export const PORTAL_CHAT_ATTACH_MAX_COMBINED_CHARS = 12_000;

const PER_DOC_MAX_CHARS = 7500;
const MIN_MEANINGFUL_EXTRACT_CHARS = 20;

const ANALYZE_UPLOAD_FOLDER = 'arko-analyse';

const ALLOWED_ANALYZE_EXT = new Set([
  'pdf', 'doc', 'docx', 'odt', 'rtf', 'txt', 'md', 'markdown',
  'html', 'htm', 'csv', 'xls', 'xlsx', 'ods', 'ppt', 'pptx', 'odp',
]);

export function formatPortalFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** `null` = ok; sonst Fehlermeldung für UI. */
export function validatePortalChatAnalyzeFile(file: File): string | null {
  if (isAudioFile(file)) return validatePortalAudioAnalyzeFile(file);
  const ext = fileExtensionFromName(file.name);
  if (!ext) return 'Dateiname ohne Erweiterung — bitte .pdf, .docx o. ä. verwenden.';
  if (!ALLOWED_ANALYZE_EXT.has(ext)) {
    return `Dateityp „.${ext}" wird im Analyse-Modus nicht unterstützt.`;
  }
  if (file.size > PORTAL_CHAT_ANALYZE_MAX_FILE_BYTES) {
    return `Zu groß (max. ${formatPortalFileSize(PORTAL_CHAT_ANALYZE_MAX_FILE_BYTES)}).`;
  }
  if (file.size === 0) return 'Leere Datei.';
  return null;
}

function truncateDocumentContextText(
  raw: string,
  limit: number,
): { text: string; truncated: boolean } {
  if (raw.length <= limit) return { text: raw, truncated: false };
  const slice = raw.slice(0, limit);
  const lastWs = slice.search(/\s\S*$/);
  const cut = lastWs > limit * 0.7 ? slice.slice(0, lastWs) : slice;
  return { text: `${cut.trimEnd()}\n…[hier abgeschnitten]`, truncated: true };
}

function decodePortalPlainTextBase64(b64: string): string {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  const body =
    out.byteLength >= 3 && out[0] === 0xef && out[1] === 0xbb && out[2] === 0xbf
      ? out.subarray(3)
      : out;
  return new TextDecoder('utf-8', { fatal: false }).decode(body);
}

function isMeaningfulExtract(text: string): boolean {
  return text.replace(/\s+/g, '').length >= MIN_MEANINGFUL_EXTRACT_CHARS;
}

async function uploadPortalFileForAnalyze(
  file: File,
  customerId: string,
  tenantExtras: Record<string, string>,
  signal?: AbortSignal,
): Promise<PortalDocumentOpenFields> {
  if (!hasPortalAuthzToken()) {
    throw new Error('Upload: Bitte neu anmelden (kein gültiges Portal-Token).');
  }
  const fd = new FormData();
  fd.append('file', file);
  fd.set('customerId', customerId);
  fd.set('folderPath', ANALYZE_UPLOAD_FOLDER);
  if (tenantExtras.tenant) fd.set('tenant', tenantExtras.tenant);

  const url = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_UPLOAD_URL);
  const res = await fetch(url, {
    method: 'POST',
    body: fd,
    credentials: 'omit',
    headers: {
      ...portalAuthzHeaders(url),
    },
    signal,
  });
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    /* leerer Body */
  }
  return parsePortalDocumentN8nResponse(data, res);
}

async function extractUploadedDocumentText(
  fields: PortalDocumentOpenFields,
  customerId: string,
  tenantExtras: Record<string, string>,
  signal?: AbortSignal,
): Promise<string> {
  const ext =
    fileExtensionFromName(fields.display_name) ||
    fileExtensionFromName(fields.nextcloud_path || '');
  const isPlain = isPortalPlainTextExtension(ext);

  if (isPlain && PORTAL_PLAINTEXT_FETCH_URL) {
    const proxyUrl = resolveOnlyofficeIssueJwtUrl(PORTAL_PLAINTEXT_FETCH_URL);
    const body: Record<string, string> = {
      document_url: fields.document_url,
      customerId,
      ...tenantExtras,
    };
    if (fields.nextcloud_path) body.nextcloud_path = fields.nextcloud_path;

    const res = await fetch(proxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...portalAuthzHeaders(proxyUrl),
      },
      credentials: 'omit',
      body: JSON.stringify(body),
      signal,
    });
    const data = (await res.json()) as {
      ok?: boolean;
      text_utf8_base64?: string;
      error?: string;
    };
    if (!res.ok || !data.ok || typeof data.text_utf8_base64 !== 'string') {
      throw new Error(
        data.error?.trim() || `Klartext-Extraktion fehlgeschlagen (${res.status}).`,
      );
    }
    return decodePortalPlainTextBase64(data.text_utf8_base64);
  }

  if (!PORTAL_ONLYOFFICE_EXTRACT_TEXT_URL) {
    throw new Error('Text-Extraktion ist auf diesem Portal nicht konfiguriert.');
  }
  if (!portalAuthzSidecarExpected()) {
    throw new Error(
      'Volltext-Extraktion ist auf diesem Portal noch nicht freigeschaltet (AuthZ).',
    );
  }

  const extractUrl = resolveOnlyofficeIssueJwtUrl(PORTAL_ONLYOFFICE_EXTRACT_TEXT_URL);
  const reqBody: Record<string, string> = {
    document_url: fields.document_url,
    display_name: fields.display_name,
    customerId,
    ...tenantExtras,
  };
  if (fields.nextcloud_path) reqBody.nextcloud_path = fields.nextcloud_path;

  const res = await fetch(extractUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...portalAuthzHeaders(extractUrl),
    },
    credentials: 'omit',
    body: JSON.stringify(reqBody),
    signal,
  });
  const data = (await res.json()) as {
    ok?: boolean;
    text_utf8_base64?: string;
    error?: string;
  };
  if (!res.ok || !data.ok || typeof data.text_utf8_base64 !== 'string') {
    if (res.status === 422) {
      throw new Error(`„${fields.display_name}": noch kein lesbarer Text (leer oder nicht unterstützt).`);
    }
    throw new Error(data.error?.trim() || `Extraktion fehlgeschlagen (${res.status}).`);
  }
  const text = decodePortalPlainTextBase64(data.text_utf8_base64);
  if (!isMeaningfulExtract(text)) {
    throw new Error(`„${fields.display_name}": zu wenig Text extrahiert.`);
  }
  return text;
}

function buildDocumentContextBlock(
  fields: PortalDocumentOpenFields,
  extractedText: string,
  perDocLimit: number,
  viaOnlyOffice: boolean,
): string {
  const title = fields.display_name.trim() || 'Dokument';
  const sourceLine = fields.nextcloud_path?.trim() || fields.document_url || '(unbekannt)';
  const ext =
    fileExtensionFromName(fields.display_name) ||
    fileExtensionFromName(fields.nextcloud_path || '') ||
    'unbekannt';
  const { text, truncated } = truncateDocumentContextText(extractedText.trim(), perDocLimit);
  const extractLabel = viaOnlyOffice ? ' via OnlyOffice ConvertService' : '';
  return [
    `Dokument: ${title}`,
    `Quelle: ${sourceLine}`,
    `Typ: ${ext}`,
    '',
    `Auszug (UTF-8${extractLabel}${truncated ? `, gekürzt auf ${perDocLimit} Zeichen` : ''}):`,
    text,
  ].join('\n');
}

export function buildAnalyzeUserDisplayText(fileNames: string[], userText: string): string {
  const list = fileNames.join(', ');
  const trimmed = userText.trim();
  const head = `Analysiere: ${list}`;
  return trimmed ? `${head}\n\n${trimmed}` : head;
}

export function buildAnalyzeChatInput(fileNames: string[], userText: string): string {
  const trimmed = userText.trim();
  if (trimmed) return trimmed;
  if (fileNames.length === 1) {
    return `Analysiere das angehängte Dokument „${fileNames[0]}" und hebe Risiken sowie Kernpunkte hervor.`;
  }
  return `Analysiere die ${fileNames.length} angehängten Dokumente und hebe Risiken sowie Kernpunkte hervor.`;
}

/**
 * Upload + Extract für alle Anhänge; liefert kombinierten documentContext für ARKO_AGENT.
 */
export async function prepareChatAnalyzeDocumentContext(args: {
  files: File[];
  customerId: string;
  tenantExtras?: Record<string, string>;
  signal?: AbortSignal;
  onProgress?: (index: number, total: number, fileName: string) => void;
  kanzlei?: string;
  email?: string;
}): Promise<{
  documentContext: string;
  fileNames: string[];
  uploadedFields: PortalDocumentOpenFields[];
}> {
  const { files, customerId, tenantExtras = {}, signal, onProgress, kanzlei, email } = args;
  if (!files.length) {
    throw new Error('Keine Dateien zum Verarbeiten.');
  }

  const audioOnly = files.every((f) => isAudioFile(f));
  if (audioOnly && files.length === 1) {
    onProgress?.(0, 1, files[0].name);
    const audioResult = await prepareAudioAnalyzeDocumentContext({
      file: files[0],
      customerId,
      kanzlei,
      email,
      tenantExtras,
      signal,
    });
    onProgress?.(1, 1, '');
    return {
      documentContext: audioResult.documentContext,
      fileNames: audioResult.fileNames,
      uploadedFields: audioResult.uploadedFields,
    };
  }

  if (files.some((f) => isAudioFile(f))) {
    throw new Error('Audiodateien bitte einzeln analysieren (nicht mit anderen Dateien mischen).');
  }

  const perDocLimit = Math.max(
    1500,
    Math.min(PER_DOC_MAX_CHARS, Math.floor(PORTAL_CHAT_ATTACH_MAX_COMBINED_CHARS / files.length)),
  );

  const blocks: string[] = [];
  const fileNames: string[] = [];
  const uploadedFields: PortalDocumentOpenFields[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    onProgress?.(i, files.length, file.name);
    const uploaded = await uploadPortalFileForAnalyze(file, customerId, tenantExtras, signal);
    uploadedFields.push(uploaded);
    const ext =
      fileExtensionFromName(uploaded.display_name) ||
      fileExtensionFromName(uploaded.nextcloud_path || '');
    const viaOnlyOffice = !isPortalPlainTextExtension(ext);
    const text = await extractUploadedDocumentText(uploaded, customerId, tenantExtras, signal);
    blocks.push(buildDocumentContextBlock(uploaded, text, perDocLimit, viaOnlyOffice));
    fileNames.push(file.name);
  }

  onProgress?.(files.length, files.length, '');

  const header =
    files.length > 1
      ? `=== Analyse-Anhänge (${files.length} Dokumente) ===`
      : '=== Analyse-Anhang ===';

  return {
    documentContext: `${header}\n\n${blocks.join('\n\n---\n\n')}`,
    fileNames,
    uploadedFields,
  };
}

export function canPortalChatAnalyzeUpload(): boolean {
  return hasPortalAuthzToken();
}
